import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Tag, Progress, Alert, Modal, Input, DatePicker, Spin, message, Tooltip, Badge, Space, Divider, Typography, Row, Col, Statistic, Select } from 'antd';
import { SyncOutlined, CloudDownloadOutlined, SettingOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, WarningOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { getCurrentMembershipYear, getPreviousMembershipYear } from '../../utils/membershipYear';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * UispSyncManager
 * Pagina per la gestione della sincronizzazione automatica tesserati e associazioni UISP
 */
const UispSyncManager = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [tokenForm, setTokenForm] = useState({ token: '', expiresAt: null, displayName: '' });
  const [savingToken, setSavingToken] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingAssociations, setSyncingAssociations] = useState(false);
  const [syncingPreviousYear, setSyncingPreviousYear] = useState(false);
  const [selectedHistoricalYear, setSelectedHistoricalYear] = useState(getPreviousMembershipYear());
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [tokenValidation, setTokenValidation] = useState(null);
  const [validatingToken, setValidatingToken] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [testingNotification, setTestingNotification] = useState(false);

  // Carica stato sincronizzazione
  const loadSyncStatus = useCallback(async () => {
    try {
      const response = await api.get('/admin/uisp-sync/status');
      if (response.data.success) {
        setSyncStatus(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento stato sync:', error);
      message.error('Errore nel caricamento dello stato');
    }
  }, []);

  // Carica storico sincronizzazioni
  const loadSyncHistory = useCallback(async () => {
    try {
      const response = await api.get('/admin/uisp-sync/history');
      if (response.data.success) {
        setSyncHistory(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento storico:', error);
    }
  }, []);

  // Carica discipline
  const loadDisciplines = useCallback(async () => {
    try {
      const response = await api.get('/admin/uisp-sync/disciplines');
      if (response.data.success) {
        setDisciplines(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento discipline:', error);
    }
  }, []);

  // Carica stato notifiche token
  const loadNotificationStatus = useCallback(async () => {
    try {
      const response = await api.get('/admin/uisp-sync/notification-status');
      if (response.data.success) {
        setNotificationStatus(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento stato notifiche:', error);
    }
  }, []);

  // Test notifica email
  const handleTestNotification = async () => {
    setTestingNotification(true);
    try {
      const response = await api.post('/admin/uisp-sync/test-notification');
      if (response.data.success) {
        message.success('Email di test inviata con successo');
        await loadNotificationStatus();
      } else {
        message.warning(response.data.message || 'Invio email non riuscito');
      }
    } catch (error) {
      console.error('Errore test notifica:', error);
      message.error('Errore nell\'invio della notifica di test');
    } finally {
      setTestingNotification(false);
    }
  };

  // Valida il token UISP con una richiesta reale all'API
  const validateToken = useCallback(async () => {
    setValidatingToken(true);
    try {
      const response = await api.post('/admin/uisp-sync/validate-token');
      if (response.data.success) {
        setTokenValidation(response.data.data);
      }
    } catch (error) {
      console.error('Errore validazione token:', error);
      setTokenValidation({ valid: null, reason: 'Errore durante la validazione' });
    } finally {
      setValidatingToken(false);
    }
  }, []);

  // Carica dati iniziali
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadSyncStatus(), loadDisciplines(), loadSyncHistory(), loadNotificationStatus()]);
      setLoading(false);
    };
    loadData();
  }, [loadSyncStatus, loadDisciplines, loadSyncHistory, loadNotificationStatus]);

  // Valida il token dopo aver caricato lo status (solo se il token esiste)
  useEffect(() => {
    if (syncStatus?.token?.hasToken && !syncStatus?.token?.expired) {
      validateToken();
    }
  }, [syncStatus?.token?.hasToken, syncStatus?.token?.expired, validateToken]);

  // Polling stato durante sincronizzazione
  useEffect(() => {
    let interval;
    if (syncing || syncStatus?.isRunning) {
      interval = setInterval(() => {
        loadSyncStatus();
        loadSyncHistory();
      }, 5000); // Poll ogni 5 secondi
    }
    return () => clearInterval(interval);
  }, [syncing, syncStatus?.isRunning, loadSyncStatus, loadSyncHistory]);

  // Reset stato syncing quando il backend termina la sincronizzazione
  useEffect(() => {
    // Se syncing è true ma syncStatus.isRunning è false, la sync è terminata
    if (syncing && syncStatus && !syncStatus.isRunning) {
      setSyncing(false);
      setSyncingPreviousYear(false);
      // Mostra messaggio di completamento
      if (syncStatus.lastSyncResult === 'success') {
        message.success('Sincronizzazione completata con successo!');
      } else if (syncStatus.lastSyncResult === 'error') {
        message.error('Sincronizzazione completata con errori');
      }
    }
  }, [syncing, syncStatus]);

  // Sincronizza solo associazioni
  const handleSyncAssociations = async () => {
    setSyncingAssociations(true);
    try {
      message.loading({ content: 'Sincronizzazione associazioni in corso...', key: 'sync-associations' });
      const response = await api.post('/admin/uisp-sync/associations');
      
      if (response.data.success) {
        const result = response.data.data;
        message.success({
          content: `Associazioni: ${result.insertedCount || 0} nuove, ${result.updatedCount || 0} aggiornate`,
          key: 'sync-associations',
          duration: 5
        });
        loadSyncStatus();
        loadSyncHistory();
      } else {
        message.error({
          content: response.data.message || 'Errore sincronizzazione associazioni',
          key: 'sync-associations'
        });
      }
    } catch (error) {
      message.error({
        content: error.response?.data?.message || 'Errore sincronizzazione associazioni',
        key: 'sync-associations'
      });
    } finally {
      setSyncingAssociations(false);
    }
  };

  // Sincronizza anno storico (una tantum)
  const handleSyncPreviousYear = async () => {
    const anno = selectedHistoricalYear;
    Modal.confirm({
      title: `Sincronizza anno ${anno}`,
      content: (
        <div>
          <p>Questa operazione importerà i dati dell'anno <strong>{anno}</strong> per avere dati statistici storici.</p>
          <p>Verranno sincronizzate prima le <strong>associazioni</strong> e poi tutti i <strong>tesserati</strong> per quell'anno.</p>
          <p>È un'operazione <strong>una tantum</strong> che potrebbe richiedere diversi minuti.</p>
          <p>I dati storici non sovrascriveranno quelli dell'anno corrente.</p>
          <p>Vuoi procedere?</p>
        </div>
      ),
      okText: 'Avvia',
      cancelText: 'Annulla',
      onOk: async () => {
        setSyncingPreviousYear(true);
        setSyncing(true);
        try {
          const response = await api.post('/admin/uisp-sync/previous-year', { anno });
          if (response.data.success) {
            message.success(`Sincronizzazione anno ${anno} avviata`);
            loadSyncStatus();
          } else {
            message.error(response.data.message || 'Errore nell\'avvio');
            setSyncing(false);
            setSyncingPreviousYear(false);
          }
        } catch (error) {
          console.error('Errore avvio sync anno storico:', error);
          message.error(error.response?.data?.message || 'Errore nell\'avvio della sincronizzazione');
          setSyncing(false);
          setSyncingPreviousYear(false);
        }
      }
    });
  };

  // Salva token
  const handleSaveToken = async () => {
    if (!tokenForm.token) {
      message.error('Token obbligatorio');
      return;
    }
    if (!tokenForm.expiresAt) {
      message.error('Data di scadenza obbligatoria');
      return;
    }

    setSavingToken(true);
    try {
      const response = await api.post('/admin/uisp-sync/token', {
        token: tokenForm.token,
        expiresAt: tokenForm.expiresAt.toISOString(),
        displayName: tokenForm.displayName || undefined
      });

      if (response.data.success) {
        setTokenModalVisible(false);
        setTokenForm({ token: '', expiresAt: null, displayName: '' });
        
        // Se il backend ha restituito la validazione, usala subito
        if (response.data.data?.validation) {
          setTokenValidation(response.data.data.validation);
          if (response.data.data.validation.valid === false) {
            message.error(`Token salvato ma non valido: ${response.data.data.validation.reason}`);
          } else if (response.data.data.validation.valid === true) {
            message.success('Token salvato e verificato ✓');
          } else {
            message.warning('Token salvato ma validazione incerta');
          }
        } else {
          message.success('Token salvato con successo');
          setTokenValidation(null);
        }
        
        await loadSyncStatus();
        
        // Ricarica validazione solo se il backend non l'ha già fornita
        if (!response.data.data?.validation) {
          await validateToken();
        }
      } else {
        message.error(response.data.message || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Errore salvataggio token:', error);
      message.error(error.response?.data?.message || 'Errore nel salvataggio del token');
    } finally {
      setSavingToken(false);
    }
  };

  // Elimina token
  const handleDeleteToken = async () => {
    Modal.confirm({
      title: 'Conferma eliminazione',
      content: 'Sei sicuro di voler eliminare il token UISP? La sincronizzazione automatica non funzionerà più.',
      okText: 'Elimina',
      okType: 'danger',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          const response = await api.delete('/admin/uisp-sync/token');
          if (response.data.success) {
            message.success('Token eliminato');
            loadSyncStatus();
          }
        } catch (error) {
          message.error('Errore nell\'eliminazione del token');
        }
      }
    });
  };

  // Avvia sincronizzazione completa
  const handleStartFullSync = async () => {
    Modal.confirm({
      title: 'Avvia sincronizzazione completa',
      content: (
        <div>
          <p>Questa operazione sincronizzerà <strong>tutte le {disciplines.length} discipline</strong> da UISP.</p>
          <p>Il processo potrebbe richiedere diversi minuti.</p>
          <p>Vuoi procedere?</p>
        </div>
      ),
      okText: 'Avvia',
      cancelText: 'Annulla',
      onOk: async () => {
        setSyncing(true);
        try {
          const payload = selectedDisciplines.length > 0 
            ? { disciplines: selectedDisciplines }
            : {};
          
          const response = await api.post('/admin/uisp-sync/start', payload);
          if (response.data.success) {
            message.success('Sincronizzazione avviata');
            loadSyncStatus();
          } else {
            message.error(response.data.message || 'Errore nell\'avvio');
            setSyncing(false);
          }
        } catch (error) {
          console.error('Errore avvio sync:', error);
          message.error(error.response?.data?.message || 'Errore nell\'avvio della sincronizzazione');
          setSyncing(false);
        }
      }
    });
  };

  // Sincronizza singola disciplina
  const handleSyncDiscipline = async (legaId, name) => {
    try {
      message.loading({ content: `Sincronizzazione ${name}...`, key: 'sync-discipline' });
      const response = await api.post(`/admin/uisp-sync/start/${legaId}`);
      
      if (response.data.success) {
        const result = response.data.data;
        message.success({
          content: `${name}: ${result.insertedCount} inseriti, ${result.updatedCount} aggiornati`,
          key: 'sync-discipline',
          duration: 5
        });
        loadSyncStatus();
      } else {
        message.error({
          content: response.data.message || `Errore sync ${name}`,
          key: 'sync-discipline'
        });
      }
    } catch (error) {
      message.error({
        content: error.response?.data?.message || `Errore sincronizzazione ${name}`,
        key: 'sync-discipline'
      });
    }
  };

  // Render stato token
  const renderTokenStatus = () => {
    const token = syncStatus?.token;
    if (!token) return null;

    if (!token.hasToken) {
      return (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Token non configurato"
          description="Configura il token UISP per abilitare la sincronizzazione automatica"
          action={
            <Button type="primary" onClick={() => setTokenModalVisible(true)}>
              Configura Token
            </Button>
          }
        />
      );
    }

    if (token.expired) {
      return (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message="Token UISP scaduto"
          description={`Il token è scaduto il ${dayjs(token.expiresAt).format('DD/MM/YYYY HH:mm')}. Effettua login su tesseramento.uisp.it e aggiorna il token.`}
          action={
            <Button type="primary" danger onClick={() => setTokenModalVisible(true)}>
              Aggiorna Token
            </Button>
          }
        />
      );
    }

    const daysLeft = token.daysUntilExpiry;
    const isExpiringSoon = daysLeft < 30;

    // Se stiamo validando il token
    if (validatingToken) {
      return (
        <Alert
          type="info"
          showIcon
          icon={<SyncOutlined spin />}
          message="Verifica token in corso..."
          description="Controllo validità del token tramite API UISP"
        />
      );
    }

    // Token rifiutato dall'API UISP (non valido anche se non scaduto localmente)
    if (tokenValidation && tokenValidation.valid === false) {
      return (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message="Token UISP non funzionante"
          description={
            <Space direction="vertical" size={0}>
              <Text type="danger">{tokenValidation.reason}</Text>
              <Text>Scadenza locale: {dayjs(token.expiresAt).format('DD/MM/YYYY HH:mm')}</Text>
              <Text type="secondary">Utente: {token.displayName}</Text>
              {tokenValidation.httpStatus && (
                <Text type="secondary">Codice HTTP: {tokenValidation.httpStatus}</Text>
              )}
            </Space>
          }
          action={
            <Space direction="vertical" size={4}>
              <Button type="primary" danger onClick={() => setTokenModalVisible(true)}>
                Aggiorna Token
              </Button>
              <Button size="small" icon={<ReloadOutlined />} onClick={validateToken} loading={validatingToken}>
                Ricontrolla
              </Button>
            </Space>
          }
        />
      );
    }

    // Token non verificabile (errore rete/timeout)
    if (tokenValidation && tokenValidation.valid === null) {
      return (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Impossibile verificare il token"
          description={
            <Space direction="vertical" size={0}>
              <Text>{tokenValidation.reason}</Text>
              <Text>Scadenza locale: {dayjs(token.expiresAt).format('DD/MM/YYYY HH:mm')} ({daysLeft} giorni rimanenti)</Text>
              <Text type="secondary">Utente: {token.displayName}</Text>
            </Space>
          }
          action={
            <Space>
              <Button size="small" icon={<ReloadOutlined />} onClick={validateToken} loading={validatingToken}>
                Ricontrolla
              </Button>
              <Button size="small" onClick={() => setTokenModalVisible(true)}>
                Modifica
              </Button>
              <Button size="small" danger onClick={handleDeleteToken}>
                Elimina
              </Button>
            </Space>
          }
        />
      );
    }

    // Token verificato e funzionante
    return (
      <Alert
        type={isExpiringSoon ? 'warning' : 'success'}
        showIcon
        icon={isExpiringSoon ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
        message={
          isExpiringSoon 
            ? `Token verificato ✓ — in scadenza tra ${daysLeft} giorni` 
            : 'Token UISP verificato e funzionante ✓'
        }
        description={
          <Space direction="vertical" size={0}>
            <Text>Scadenza: {dayjs(token.expiresAt).format('DD/MM/YYYY HH:mm')}</Text>
            <Text type="secondary">Utente: {token.displayName}</Text>
            {tokenValidation?.responseSize && (
              <Text type="secondary">Ultima verifica: risposta API OK ({(tokenValidation.responseSize / 1024).toFixed(1)} KB)</Text>
            )}
          </Space>
        }
        action={
          <Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={validateToken} loading={validatingToken}>
              Riverifica
            </Button>
            <Button size="small" onClick={() => setTokenModalVisible(true)}>
              Modifica
            </Button>
            <Button size="small" danger onClick={handleDeleteToken}>
              Elimina
            </Button>
          </Space>
        }
      />
    );
  };

  // Render stato sincronizzazione
  const renderSyncStatus = () => {
    if (!syncStatus) return null;

    if (syncStatus.isRunning) {
      // Mappa legaId → nome disciplina
      const disciplineMap = Object.fromEntries(disciplines.map(d => [d.legaId, d.name]));

      // Discipline già processate (presenti nello storico), ordinate per data
      const processedList = Object.entries(syncHistory?.disciplines || {})
        .map(([legaId, d]) => ({ legaId, name: disciplineMap[legaId] || legaId, ...d }))
        .sort((a, b) => new Date(a.lastSync) - new Date(b.lastSync));

      return (
        <Card className="mb-3" style={{ borderColor: '#1890ff' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <SyncOutlined spin style={{ fontSize: 24, color: '#1890ff' }} />
              <Title level={4} style={{ margin: 0 }}>Sincronizzazione in corso...</Title>
            </Space>

            {syncStatus.currentDiscipline && (
              <Text>
                <SyncOutlined spin style={{ color: '#1890ff', marginRight: 6 }} />
                Disciplina attuale: <strong>{syncStatus.currentDiscipline}</strong>
              </Text>
            )}

            <Progress
              percent={syncStatus.progress?.percentage || 0}
              status="active"
              format={() => `${syncStatus.progress?.completed || 0}/${syncStatus.progress?.total || 0}`}
            />

            {processedList.length > 0 && (
              <div
                style={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  padding: '6px 10px',
                  background: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  lineHeight: '22px'
                }}
              >
                {processedList.map(d => (
                  <div key={d.legaId} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
                    {d.success ? (
                      d.skipped
                        ? <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>SKIP</Tag>
                        : <Tag color="green" style={{ margin: 0, fontSize: 11 }}>OK</Tag>
                    ) : (
                      <Tag color="red" style={{ margin: 0, fontSize: 11 }}>ERR</Tag>
                    )}
                    <span style={{ flex: 1 }}>{d.name}</span>
                    {!d.skipped && d.success && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        +{d.insertedCount} ~{d.updatedCount}
                        {d.errorCount > 0 && <span style={{ color: '#ff4d4f' }}> ✗{d.errorCount}</span>}
                      </Text>
                    )}
                    {!d.success && d.reason && (
                      <Text type="danger" style={{ fontSize: 11 }}>{d.reason}</Text>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Space>
        </Card>
      );
    }

    if (syncStatus.lastSync) {
      return (
        <Card className="mb-3" size="small">
          <Space>
            {syncStatus.lastSyncResult === 'success' ? (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
            )}
            <Text>
              Ultima sincronizzazione: <strong>{dayjs(syncStatus.lastSync).format('DD/MM/YYYY HH:mm')}</strong>
              {' - '}
              <Tag color={syncStatus.lastSyncResult === 'success' ? 'green' : 'red'}>
                {syncStatus.lastSyncResult === 'success' ? 'Completata' : 'Errori'}
              </Tag>
            </Text>
          </Space>
        </Card>
      );
    }

    return null;
  };

  // Colonne tabella discipline
  const disciplineColumns = [
    {
      title: 'ID',
      dataIndex: 'legaId',
      key: 'legaId',
      width: 100,
      render: (id) => <code>{id}</code>
    },
    {
      title: 'Disciplina',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Tooltip title="Sincronizza solo questa disciplina">
          <Button
            type="primary"
            size="small"
            icon={<SyncOutlined />}
            onClick={() => handleSyncDiscipline(record.legaId, record.name)}
            disabled={syncStatus?.isRunning || !syncStatus?.token?.hasToken || syncStatus?.token?.expired}
          >
            Sync
          </Button>
        </Tooltip>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Caricamento..." />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Title level={2} style={{ color: '#006e39', margin: 0 }}>
            <CloudDownloadOutlined className="me-2" />
            Sincronizzazione UISP
          </Title>
          <Text type="secondary">
            Sincronizzazione automatica di associazioni e tesserati da tesseramento.uisp.it
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadSyncStatus(); loadDisciplines(); loadSyncHistory(); setTokenValidation(null); }}>
            Ricarica
          </Button>
          <Select
            value={selectedHistoricalYear}
            onChange={setSelectedHistoricalYear}
            style={{ width: 90 }}
            disabled={syncStatus?.isRunning || !syncStatus?.token?.hasToken || syncStatus?.token?.expired || syncingPreviousYear}
            options={Array.from(
              { length: new Date().getFullYear() - 2019 },
              (_, i) => { const y = getCurrentMembershipYear() - i; return { value: y, label: String(y) }; }
            )}
          />
          <Button 
            icon={<ClockCircleOutlined />}
            onClick={handleSyncPreviousYear}
            disabled={syncStatus?.isRunning || !syncStatus?.token?.hasToken || syncStatus?.token?.expired}
            loading={syncingPreviousYear}
          >
            Sync Anno {selectedHistoricalYear}
          </Button>
          <Button 
            type="primary" 
            size="large"
            icon={<PlayCircleOutlined />}
            onClick={handleStartFullSync}
            disabled={syncStatus?.isRunning || !syncStatus?.token?.hasToken || syncStatus?.token?.expired}
            loading={syncing && !syncingPreviousYear}
          >
            Sincronizza Tutto
          </Button>
        </Space>
      </div>

      {/* Token Status */}
      <div className="mb-4">
        {renderTokenStatus()}
      </div>

      {/* Notification Status */}
      {notificationStatus?.tokenStatus?.hasToken && notificationStatus?.recipients?.length > 0 && (
        <Card size="small" className="mb-4" title={<><ClockCircleOutlined /> Notifiche Scadenza Token</>}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space direction="vertical" size={4}>
                <Text>
                  Destinatari: <strong>{notificationStatus.recipients.join(', ')}</strong>
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Notifiche inviate a: 30, 14, 7, 3, 1 giorno/i prima della scadenza.
                  Dopo la scadenza: promemoria giornaliero.
                </Text>
                {notificationStatus.notificationState?.lastDailyReminder && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Ultimo promemoria giornaliero: {dayjs(notificationStatus.notificationState.lastDailyReminder).format('DD/MM/YYYY')}
                  </Text>
                )}
              </Space>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleTestNotification}
                loading={testingNotification}
                size="small"
              >
                Invia Test
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Sync Status */}
      {renderSyncStatus()}

      {/* Info Cards */}
      <Row gutter={16} className="mb-4">
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Discipline configurate"
              value={disciplines.length}
              prefix={<i className="bi bi-list-check" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Prossima sync automatica"
              value="02:00"
              prefix={<ClockCircleOutlined />}
              suffix="ogni giorno"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Regione"
              value="PUGLIA"
              prefix={<i className="bi bi-geo-alt" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Ultima sync completa"
              value={syncHistory?.lastFullSync?.timestamp ? dayjs(syncHistory.lastFullSync.timestamp).format('DD/MM HH:mm') : '-'}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Associazioni Card */}
      <Card 
        title={
          <Space>
            <i className="bi bi-building" />
            <span>Associazioni UISP</span>
          </Space>
        }
        className="mb-4"
        extra={
          <Button 
            type="primary" 
            icon={<SyncOutlined spin={syncingAssociations} />}
            onClick={handleSyncAssociations}
            disabled={syncStatus?.isRunning || !syncStatus?.token?.hasToken || syncStatus?.token?.expired || syncingAssociations}
            loading={syncingAssociations}
          >
            Sincronizza Associazioni
          </Button>
        }
      >
        {syncHistory?.associations ? (
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="Ultima sincronizzazione" 
                value={dayjs(syncHistory.associations.lastSync).format('DD/MM/YYYY HH:mm')} 
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Nuove inserite" 
                value={syncHistory.associations.insertedCount || 0} 
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Aggiornate" 
                value={syncHistory.associations.updatedCount || 0} 
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Totale processate" 
                value={syncHistory.associations.totalCount || 0} 
              />
            </Col>
          </Row>
        ) : (
          <Alert
            type="info"
            message="Nessuna sincronizzazione effettuata"
            description="Le associazioni verranno sincronizzate automaticamente prima dei tesserati durante la sincronizzazione completa."
          />
        )}
      </Card>

      {/* Disciplines Table */}
      <Card 
        title={
          <Space>
            <i className="bi bi-trophy" />
            <span>Discipline UISP</span>
            <Badge count={disciplines.length} style={{ backgroundColor: '#006e39' }} />
          </Space>
        }
      >
        <Table
          dataSource={disciplines.map(d => ({
            ...d,
            lastSync: syncHistory?.disciplines?.[d.legaId]
          }))}
          columns={[
            ...disciplineColumns.slice(0, 2),
            {
              title: 'Ultima Sync',
              key: 'lastSync',
              width: 180,
              render: (_, record) => {
                const hist = record.lastSync;
                if (!hist) return <Text type="secondary">Mai</Text>;
                return (
                  <Tooltip title={`${hist.totalCount} tesserati, ${hist.insertedCount} nuovi, ${hist.updatedCount} aggiornati`}>
                    <Space size={4}>
                      {hist.success ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      )}
                      <Text>{dayjs(hist.lastSync).format('DD/MM HH:mm')}</Text>
                      {hist.skipped && <Tag color="orange" size="small">Vuota</Tag>}
                    </Space>
                  </Tooltip>
                );
              }
            },
            {
              title: 'Tesserati',
              key: 'count',
              width: 100,
              render: (_, record) => {
                const hist = record.lastSync;
                if (!hist || hist.skipped) return '-';
                return hist.totalCount || 0;
              }
            },
            disciplineColumns[2] // Azioni
          ]}
          rowKey="legaId"
          pagination={{ pageSize: 15 }}
          size="small"
          rowSelection={{
            selectedRowKeys: selectedDisciplines,
            onChange: setSelectedDisciplines,
          }}
        />
        {selectedDisciplines.length > 0 && (
          <Alert
            type="info"
            message={`${selectedDisciplines.length} discipline selezionate`}
            description="Clicca 'Sincronizza Tutto' per sincronizzare solo le discipline selezionate"
            className="mt-3"
          />
        )}
      </Card>

      {/* Come funziona */}
      <Card title="Come funziona" className="mt-4" size="small">
        <ol>
          <li><strong>Configura Token</strong>: Accedi a <a href="https://tesseramento.uisp.it" target="_blank" rel="noopener noreferrer">tesseramento.uisp.it</a>, completa il captcha, poi copia il token dai cookie del browser. <em>Nota: il token ha validità variabile (generalmente qualche mese), controlla la scadenza effettiva.</em></li>
          <li><strong>Sincronizzazione automatica</strong>: Ogni giorno alle 02:00, il sistema scarica automaticamente i tesserati da UISP.</li>
          <li><strong>Notifiche scadenza</strong>: Ricevi email di avviso a 30, 14, 7, 3 e 1 giorno prima della scadenza del token. Dopo la scadenza, ricevi un promemoria giornaliero.</li>
          <li><strong>Sincronizzazione manuale</strong>: Puoi avviare la sincronizzazione in qualsiasi momento cliccando "Sincronizza Tutto".</li>
          <li><strong>Singola disciplina</strong>: Puoi sincronizzare una sola disciplina cliccando "Sync" nella tabella.</li>
          <li><strong>Sync Anno Storico</strong>: Per avere dati statistici storici, seleziona l'anno desiderato (dal 2020 in poi) e clicca "Sync Anno [anno]". Verranno sincronizzate prima le associazioni e poi tutti i tesserati per quell'anno. È un'operazione una tantum: i dati storici non sovrascrivono quelli dell'anno corrente.</li>
        </ol>
      </Card>

      {/* Modal Token */}
      <Modal
        title={<><SettingOutlined /> Configura Token UISP</>}
        open={tokenModalVisible}
        onCancel={() => setTokenModalVisible(false)}
        onOk={handleSaveToken}
        confirmLoading={savingToken}
        okText="Salva"
        cancelText="Annulla"
        width={600}
      >
        <Alert
          type="info"
          message="Come ottenere il token"
          description={
            <ol className="mb-0 ps-3">
              <li>Vai su <a href="https://tesseramento.uisp.it" target="_blank" rel="noopener noreferrer">tesseramento.uisp.it</a></li>
              <li>Effettua il login completando il captcha</li>
              <li>Apri gli strumenti sviluppatore (F12) → Application → Cookies</li>
              <li>Copia il valore del cookie che inizia con "eyJQYXlMb2Fk..."</li>
            </ol>
          }
          className="mb-3"
        />

        <div className="mb-3">
          <label className="form-label fw-bold">Token UISP *</label>
          <TextArea
            rows={4}
            placeholder="eyJQYXlMb2FkIjp7IkNvbnRhYmlsaXRhQWNjZXNzIjo..."
            value={tokenForm.token}
            onChange={(e) => setTokenForm({ ...tokenForm, token: e.target.value })}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Data scadenza *</label>
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Seleziona data scadenza"
            value={tokenForm.expiresAt}
            onChange={(date) => setTokenForm({ ...tokenForm, expiresAt: date })}
            format="DD/MM/YYYY"
            disabledDate={(current) => current && current < dayjs().startOf('day')}
          />
          <small className="text-muted">
            La validità del token è variabile (generalmente qualche mese). Controlla la scadenza effettiva dopo ogni rinnovo.
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label">Nome visualizzato (opzionale)</label>
          <Input
            placeholder="es: PUGLIA Comitato PUGLIA Comitato Regionale"
            value={tokenForm.displayName}
            onChange={(e) => setTokenForm({ ...tokenForm, displayName: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
};

export default UispSyncManager;
